import type { StrapiSamplingPlanCreateData, StrapiSampleInformationRow } from '../../api/strapiCrm';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import type { SamplingPlanAirGroup } from './samplingPlanAirGroup';

function relationOne(row: DynamicEntityRow | null | undefined, field: string): DynamicEntityRow | null {
  const raw = row?.[field];
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as DynamicEntityRow) : null;
}

/** Plan khusus form checklist air/non-air (bukan plan per baris sampel untuk survey). */
export function isAirGroupChecklistPlan(plan: DynamicEntityRow | null | undefined): boolean {
  const g = String(plan?.air_group ?? '').trim();
  return g === 'air' || g === 'non_air';
}

export function sampleLineSamplePointCount(line: StrapiSampleInformationRow): number {
  if (line.sample_point_count != null && Number.isFinite(Number(line.sample_point_count))) {
    return Math.max(1, Math.floor(Number(line.sample_point_count)));
  }
  return 1;
}

export function findSamplingPlanForSampleLine(
  line: StrapiSampleInformationRow,
  idx: number,
  plans: DynamicEntityRow[],
): DynamicEntityRow | null {
  const linePlans = plans.filter((plan) => !isAirGroupChecklistPlan(plan));
  const lineDocId = line.documentId?.trim() ?? '';
  const jenisDocId = line.jenis_pengukuran?.documentId?.trim() ?? '';

  const byLine =
    lineDocId &&
    linePlans.find((plan) => {
      const linked = relationOne(plan, 'quotation_sample_information');
      return String(linked?.documentId ?? '').trim() === lineDocId;
    });
  if (byLine) return byLine;

  if (jenisDocId) {
    const byJenis = linePlans.find((plan) => {
      const bidang = relationOne(plan, 'bidang_pengujian');
      return String(bidang?.documentId ?? '').trim() === jenisDocId;
    });
    if (byJenis) return byJenis;
  }

  return linePlans.find((plan) => Number(plan.line_index) === idx) ?? null;
}

export function findSamplingPlanForAirGroup(
  plans: DynamicEntityRow[],
  airGroup: SamplingPlanAirGroup,
): DynamicEntityRow | null {
  return plans.find((plan) => String(plan.air_group ?? '').trim() === airGroup) ?? null;
}

export function buildSamplingPlanCreateDataForSampleLine(
  workOrderDocumentId: string,
  quotationDocumentId: string | null,
  line: StrapiSampleInformationRow,
  idx: number,
): StrapiSamplingPlanCreateData {
  const lineDocId = line.documentId?.trim();
  const groupDocId = line.quotation_group?.documentId?.trim();
  const jenisDocId = line.jenis_pengukuran?.documentId?.trim();
  const quotationDocId = quotationDocumentId?.trim() ?? '';

  return {
    work_order: { connect: [workOrderDocumentId] },
    ...(quotationDocId ? { quotation: { connect: [quotationDocId] } } : {}),
    ...(lineDocId ? { quotation_sample_information: { connect: [lineDocId] } } : {}),
    ...(groupDocId ? { quotation_group: { connect: [groupDocId] } } : {}),
    ...(jenisDocId ? { bidang_pengujian: { connect: [jenisDocId] } } : {}),
    line_index: idx,
    sample_point_count: sampleLineSamplePointCount(line),
    progress_status: 'draft',
  };
}

/** Satu plan checklist untuk seluruh jenis pengukuran dalam group air / non_air. */
export function buildSamplingPlanCreateDataForAirGroup(
  workOrderDocumentId: string,
  quotationDocumentId: string | null,
  airGroup: SamplingPlanAirGroup,
): StrapiSamplingPlanCreateData {
  const quotationDocId = quotationDocumentId?.trim() ?? '';
  return {
    work_order: { connect: [workOrderDocumentId] },
    ...(quotationDocId ? { quotation: { connect: [quotationDocId] } } : {}),
    air_group: airGroup,
    /** Jauh dari indeks baris sampel agar tidak bentrok dengan matching line_index. */
    line_index: airGroup === 'air' ? 9000 : 9001,
    sample_point_count: 1,
    progress_status: 'draft',
  };
}
